import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCartSnapshot } from "../hooks/useCart";
import "../style/product_category.css";
import { routePaths } from "../constants/routes";
import { addToCart } from "../services/cartService";
import { FALLBACK_PRODUCT_IMAGE, fetchCategories, fetchProducts } from "../services/commerceApi";

export default function ProductPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { cartIds, syncCartSnapshot } = useCartSnapshot();

  const handleAddToCart = (event, product) => {
    event.preventDefault();
    event.stopPropagation();

    if (cartIds.has(String(product.id))) {
      navigate(routePaths.cart);
      return;
    }

    addToCart(product);
    syncCartSnapshot();
    toast.success(`${product.name} added to cart.`);
  };

  useEffect(() => {
    let isMounted = true;

    async function loadStorefront() {
      try {
        const [categoriesResponse, flashSalesResponse, featuredResponse] =
          await Promise.all([
            fetchCategories(),
            fetchProducts({ flashSale: true, limit: 50 }),
            fetchProducts({ featured: true, limit: 50 }),
          ]);

        if (!isMounted) {
          return;
        }

        setCategories(categoriesResponse);
        setFlashSales(flashSalesResponse);
        setTopSellers(featuredResponse);
      } catch (error) {
        console.error("Unable to load storefront data", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadStorefront();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="react-body-wrapper storefront-page">
      <section className="storefront-hero">
        <div>
          <p className="storefront-eyebrow">QA Coder Catalog</p>
          <h1>Browse a product catalog built for real ecommerce automation practice</h1>
          <p className="storefront-copy">
            Use the catalog to validate home page widgets, product search, category listing,
            flash sales, add to cart, address, payment, invoice, success, and tracking flows.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              navigate(`${routePaths.productSearch}?q=${encodeURIComponent(searchQuery.trim())}`);
            }}
            style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "12px", marginTop: "20px", maxWidth: "720px" }}
          >
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search product, brand, or category"
              style={{
                minHeight: "48px",
                padding: "12px 16px",
                borderRadius: "12px",
                border: "1px solid rgba(148,163,184,0.5)",
              }}
            />
            <button
              type="submit"
              style={{
                minHeight: "48px",
                padding: "12px 18px",
                borderRadius: "12px",
                border: "none",
                background: "#ea580c",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Search Listing
            </button>
          </form>
        </div>
        <div className="storefront-highlight">
          <strong>{categories.length || 4} categories</strong>
          <span>{flashSales.length + topSellers.length} highlighted products loaded</span>
          <small>Ready for catalog, search, cart, and checkout validation</small>
        </div>
      </section>

      <section className="flash-sales-section">
        <div className="flash-sales-header">
          <h2 className="flash-sales-title">
            <i className="fas fa-bolt"></i> Flash Sales
          </h2>
          <div className="timer-container">Live database-backed product offers</div>
          <NavLink to="/flash-sales" className="see-all">
            See All <i className="fas fa-chevron-right"></i>
          </NavLink>
        </div>

        <div className="flash-sales-products">
          {loading && <p>Loading flash sale products...</p>}
          {!loading && flashSales.length === 0 && (
            <div className="empty-state-card">No flash sale products are available right now.</div>
          )}

          {!loading &&
            flashSales.map((product) => (
              <NavLink to={`/product-details/${product.id}`} className="product-card" key={product.id}>
                <span className="discount-badge">-{product.discount}%</span>
                <img
                  src={product.listingImageUrl || product.url || FALLBACK_PRODUCT_IMAGE}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                />
                <h3 className="product-name">{product.name}</h3>
                <p className="product-card-copy">{product.description}</p>
                <p className="current-price">{product.currentPriceFormatted}</p>
                <p className="original-price">{product.originalPriceFormatted}</p>
                <span className="items-left">{product.stock} items left</span>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.round((product.stock / Math.max(product.maxStock, 1)) * 100)
                      )}%`,
                    }}
                  ></div>
                </div>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  <button
                    type="button"
                    onClick={(event) => handleAddToCart(event, product)}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      borderRadius: "10px",
                      border: "none",
                      background: "#1d4ed8",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {cartIds.has(String(product.id)) ? "Go to Cart" : "Add to Cart"}
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate(routePaths.productDetails(product.id));
                    }}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      borderRadius: "10px",
                      border: "none",
                      background: "#ea580c",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Buy Now
                  </button>
                </div>
              </NavLink>
            ))}
        </div>
      </section>

      <section className="category-grid-section">
        <div className="section-title-row">
          <div>
            <p className="storefront-section-label">Shop by category</p>
            <h2>Choose a catalog area</h2>
          </div>
        </div>
        <div className="category-grid">
          {categories.map((category) => (
            <NavLink
              to={`/product_phone_tablets?category=${encodeURIComponent(category.category)}`}
              className="category-card"
              key={category.category}
            >
              <img
                src={category.imageUrl || FALLBACK_PRODUCT_IMAGE}
                alt={category.category}
                loading="lazy"
                decoding="async"
              />
              <p className="category-name">{category.category}</p>
              <small>{category.productCount} products</small>
            </NavLink>
          ))}
        </div>
      </section>

      <section className="top-sellers-section">
        <div className="top-sellers-header">
          <h2 className="top-sellers-title">Top Sellers</h2>
          <NavLink to="/product_phone_tablets" className="see-all">
            See All <i className="fas fa-chevron-right"></i>
          </NavLink>
        </div>

        <div className="top-sellers-products">
          {!loading && topSellers.length === 0 && (
            <div className="empty-state-card">Featured products could not be loaded.</div>
          )}
          {topSellers.map((product) => (
            <NavLink to={`/product-details/${product.id}`} className="product-card" key={product.id}>
              <span className="discount-badge">-{product.discount}%</span>
              <img
                src={product.listingImageUrl || product.url || FALLBACK_PRODUCT_IMAGE}
                alt={product.name}
                loading="lazy"
                decoding="async"
              />
              <h3 className="product-name">{product.name}</h3>
              <p className="product-card-copy">{product.description}</p>
              <p className="current-price">{product.currentPriceFormatted}</p>
              <p className="original-price">{product.originalPriceFormatted}</p>
              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={(event) => handleAddToCart(event, product)}
                  style={{
                    flex: 1,
                    minHeight: "40px",
                    borderRadius: "10px",
                    border: "none",
                    background: "#1d4ed8",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {cartIds.has(String(product.id)) ? "Go to Cart" : "Add to Cart"}
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(routePaths.productDetails(product.id));
                  }}
                  style={{
                    flex: 1,
                    minHeight: "40px",
                    borderRadius: "10px",
                    border: "none",
                    background: "#ea580c",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Buy Now
                </button>
              </div>
            </NavLink>
          ))}
        </div>
      </section>
    </div>
  );
}
